import { useEffect, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import PlanBuilder from "./PlanBuilder";

export default function ClientDetailDrawer({ client, onClose, defaultTab = "plan" }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get(`/admin/clients/${client.id}`).then(({ data }) => setDetail(data)).catch(() => {});
  }, [client.id]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="client-detail-dialog" className="bg-[#141414] border-[#27272A] text-white max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-2xl">{client.name}</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue={defaultTab}>
          <TabsList className="bg-[#0A0A0A]">
            <TabsTrigger data-testid="client-tab-plan" value="plan">
              Workout Plan
            </TabsTrigger>
            <TabsTrigger data-testid="client-tab-waiver" value="waiver">
              Waiver
            </TabsTrigger>
            <TabsTrigger data-testid="client-tab-checkins" value="checkins">
              Check-ins
            </TabsTrigger>
          </TabsList>
          <TabsContent value="plan">
            <PlanBuilder clientId={client.id} initialPlan={detail?.plan} />
          </TabsContent>
          <TabsContent value="waiver">
            {detail?.waiver ? (
              <div data-testid="client-waiver-info" className="space-y-3 text-sm text-[#D4D4D8] mt-4">
                <p>
                  <span className="text-[#71717A]">Full Name:</span> {detail.waiver.full_name}
                </p>
                <p>
                  <span className="text-[#71717A]">Emergency Contact:</span> {detail.waiver.emergency_contact} ({detail.waiver.emergency_phone})
                </p>
                <p>
                  <span className="text-[#71717A]">Medical Conditions:</span> {detail.waiver.medical_conditions || "None reported"}
                </p>
                <p>
                  <span className="text-[#71717A]">Signed:</span> {new Date(detail.waiver.signed_at).toLocaleString()}
                </p>
                {detail.waiver.parq_flagged && (
                  <div data-testid="client-waiver-parq-flagged" className="bg-amber-500/10 border border-amber-500/40 rounded-xl px-4 py-3 text-amber-300 text-sm">
                    Client flagged at least one PAR-Q risk factor — recommend confirming medical clearance before intense training.
                  </div>
                )}
                {!!detail.waiver.parq_answers && Object.keys(detail.waiver.parq_answers).length > 0 && (
                  <div className="pt-1">
                    <p className="text-[#71717A] mb-2">PAR-Q Answers:</p>
                    <div className="space-y-1.5">
                      {Object.entries(detail.waiver.parq_answers).map(([key, val]) => (
                        <p key={key} data-testid={`client-parq-answer-${key}`} className="text-xs flex items-center justify-between border-b border-[#27272A] pb-1.5">
                          <span className="text-[#A1A1AA] capitalize">{key.replace(/_/g, " ")}</span>
                          <span className={val ? "text-amber-300 font-semibold" : "text-[#71717A]"}>{val ? "Yes" : "No"}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[#71717A] mt-4">Waiver not yet signed.</p>
            )}
          </TabsContent>
          <TabsContent value="checkins">
            <div className="space-y-4 mt-4">
              {detail?.checkins?.length ? (
                detail.checkins.map((c) => (
                  <div key={c.id} data-testid={`checkin-record-${c.id}`} className="border border-[#27272A] rounded-xl p-4">
                    <p className="text-xs text-[#71717A] mb-1">
                      {new Date(c.created_at).toLocaleDateString()} {c.weight ? `\u00b7 ${c.weight} lbs` : ""}
                    </p>
                    <p className="text-sm text-[#D4D4D8] mb-2">{c.feedback}</p>
                    {(c.photo_before_file_id || c.photo_after_file_id) && (
                      <div className="flex gap-3 mb-2">
                        {c.photo_before_file_id && (
                          <div className="relative">
                            <img src={fileUrl(c.photo_before_file_id)} className="w-20 h-20 object-cover rounded-lg" alt="before" />
                            <span className="absolute top-1 left-1 bg-black/70 text-[#D4AF37] text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full">Before</span>
                          </div>
                        )}
                        {c.photo_after_file_id && (
                          <div className="relative">
                            <img src={fileUrl(c.photo_after_file_id)} className="w-20 h-20 object-cover rounded-lg" alt="after" />
                            <span className="absolute top-1 left-1 bg-black/70 text-[#D4AF37] text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full">After</span>
                          </div>
                        )}
                      </div>
                    )}
                    {!!c.photo_file_ids?.length && (
                      <div className="flex gap-2">
                        {c.photo_file_ids.map((fid) => (
                          <img key={fid} src={fileUrl(fid)} className="w-16 h-16 object-cover rounded-lg" alt="progress" />
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-[#71717A]">No check-ins yet.</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
